import {
  EExternalApiLimitBucket,
  EExternalApiLimitEndpoint,
  EExternalApiLimitManagedBy,
  EExternalApiLimitMetric,
  EExternalApiLimitTargetScope,
  type ICreateExternalApiLimitRuleBody,
  type IExternalApiLimitRule,
  type IOrganizationMember,
  type IParsedProject,
} from "../types";
import { formatDeviceLocaleNumber } from "./number";
import { toWesternDigits } from "./western-digits";

// NOTE: NSAI-7442 / NSAI-7443 — everything about External API limit rules that
// web and mobile must agree on: who may do what, which choices a form offers,
// how a window is shown, and what a valid rule is. The server
// (apps/nsai/server/src/external-api-limits/) is the authority on every one of
// these; this file only decides what the UI offers, so the two clients cannot
// drift into showing a control the server refuses.

// NOTE: The same caps as EXTERNAL_API_LIMIT_MAX_* in
// apps/nsai/server/src/external-api-limits/external-api-limits.constants.ts —
// change both together, or the form accepts what the API refuses.
export const EXTERNAL_API_LIMIT_MAX_WINDOW_SECONDS = 31 * 24 * 60 * 60;
export const EXTERNAL_API_LIMIT_MAX_LIMIT_VALUE = 1_000_000_000_000;
export const EXTERNAL_API_LIMIT_MAX_MODEL_NAMES = 50;
export const EXTERNAL_API_LIMIT_MAX_MODEL_NAME_LENGTH = 100;
export const EXTERNAL_API_LIMIT_MAX_NAME_LENGTH = 120;

// NOTE: Mirrors EXTERNAL_API_LIMIT_ALLOWED_BUCKETS on the server: a counter is
// never split coarser than the rule's target.
export const EXTERNAL_API_LIMIT_ALLOWED_BUCKETS: Readonly<
  Record<EExternalApiLimitTargetScope, readonly EExternalApiLimitBucket[]>
> = {
  [EExternalApiLimitTargetScope.PLATFORM]: [
    EExternalApiLimitBucket.SHARED,
    EExternalApiLimitBucket.ORGANIZATION,
    EExternalApiLimitBucket.PROJECT,
    EExternalApiLimitBucket.USER,
    EExternalApiLimitBucket.API_KEY,
    EExternalApiLimitBucket.IP,
  ],
  [EExternalApiLimitTargetScope.ORGANIZATION]: [
    EExternalApiLimitBucket.SHARED,
    EExternalApiLimitBucket.PROJECT,
    EExternalApiLimitBucket.USER,
    EExternalApiLimitBucket.API_KEY,
    EExternalApiLimitBucket.IP,
  ],
  [EExternalApiLimitTargetScope.PROJECT]: [
    EExternalApiLimitBucket.SHARED,
    EExternalApiLimitBucket.USER,
    EExternalApiLimitBucket.API_KEY,
    EExternalApiLimitBucket.IP,
  ],
  [EExternalApiLimitTargetScope.USER]: [
    EExternalApiLimitBucket.SHARED,
    EExternalApiLimitBucket.API_KEY,
    EExternalApiLimitBucket.IP,
  ],
  [EExternalApiLimitTargetScope.API_KEY]: [
    EExternalApiLimitBucket.SHARED,
    EExternalApiLimitBucket.IP,
  ],
};

// NOTE: The targets an organization may pick — `platform` is the operator's.
export const ORGANIZATION_EXTERNAL_API_LIMIT_TARGET_SCOPES: readonly EExternalApiLimitTargetScope[] =
  [
    EExternalApiLimitTargetScope.ORGANIZATION,
    EExternalApiLimitTargetScope.PROJECT,
    EExternalApiLimitTargetScope.USER,
    EExternalApiLimitTargetScope.API_KEY,
  ];

export const EXTERNAL_API_LIMIT_METRICS: readonly EExternalApiLimitMetric[] =
  Object.values(EExternalApiLimitMetric);

export const EXTERNAL_API_LIMIT_ENDPOINTS: readonly EExternalApiLimitEndpoint[] =
  Object.values(EExternalApiLimitEndpoint);

export enum EExternalApiLimitWindowUnit {
  SECOND = "second",
  MINUTE = "minute",
  HOUR = "hour",
  DAY = "day",
}

export const EXTERNAL_API_LIMIT_WINDOW_UNIT_SECONDS: Readonly<
  Record<EExternalApiLimitWindowUnit, number>
> = {
  [EExternalApiLimitWindowUnit.SECOND]: 1,
  [EExternalApiLimitWindowUnit.MINUTE]: 60,
  [EExternalApiLimitWindowUnit.HOUR]: 60 * 60,
  [EExternalApiLimitWindowUnit.DAY]: 24 * 60 * 60,
};

// NOTE: Largest first, so a window is shown in the unit a person would say.
const WINDOW_UNITS_DESCENDING: readonly EExternalApiLimitWindowUnit[] = [
  EExternalApiLimitWindowUnit.DAY,
  EExternalApiLimitWindowUnit.HOUR,
  EExternalApiLimitWindowUnit.MINUTE,
  EExternalApiLimitWindowUnit.SECOND,
];

export interface IExternalApiLimitWindow {
  amount: number;
  unit: EExternalApiLimitWindowUnit;
}

// NOTE: 86400 reads as "1 day", 5400 as "90 minutes": the largest unit that
// divides the window exactly, so nothing is rounded away.
export const toExternalApiLimitWindow = (
  windowSeconds: number,
): IExternalApiLimitWindow => {
  const unit =
    WINDOW_UNITS_DESCENDING.find(
      (candidate) =>
        windowSeconds % EXTERNAL_API_LIMIT_WINDOW_UNIT_SECONDS[candidate] === 0,
    ) ?? EExternalApiLimitWindowUnit.SECOND;

  return {
    amount: windowSeconds / EXTERNAL_API_LIMIT_WINDOW_UNIT_SECONDS[unit],
    unit,
  };
};

export const fromExternalApiLimitWindow = ({
  amount,
  unit,
}: IExternalApiLimitWindow): number =>
  amount * EXTERNAL_API_LIMIT_WINDOW_UNIT_SECONDS[unit];

export const isWindowedExternalApiLimitMetric = (
  metric: EExternalApiLimitMetric,
): boolean => metric !== EExternalApiLimitMetric.CONCURRENT_REQUESTS;

export interface IExternalApiLimitsAccess {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}

interface IExternalApiLimitsAccessProps {
  isOwner: boolean;
  projects: IParsedProject[];
}

// NOTE: Owners pass on every level, as the server grants them. A member holds
// a level when any of their project roles in the organization holds it — the
// same organization-scoped check the server runs.
export const getExternalApiLimitsAccess = ({
  isOwner,
  projects,
}: IExternalApiLimitsAccessProps): IExternalApiLimitsAccess => {
  if (isOwner) {
    return { canRead: true, canWrite: true, canDelete: true };
  }

  return {
    canRead: projects.some(
      (project) => project.permissions.canReadExternalApiLimits,
    ),
    canWrite: projects.some(
      (project) => project.permissions.canWriteExternalApiLimits,
    ),
    canDelete: projects.some(
      (project) => project.permissions.canDeleteExternalApiLimits,
    ),
  };
};

interface IExternalApiLimitTargetChoicesProps {
  isOwner: boolean;
  projects: IParsedProject[];
}

export interface IExternalApiLimitTargetChoices {
  scopes: readonly EExternalApiLimitTargetScope[];
  keyProjects: IParsedProject[];
}

// NOTE: An API key is picked from its project's key list, which the server
// serves only with api_key:read in that project — a level the limits family
// does not imply. So a member is offered the key target through the projects
// whose keys they may read, and not at all where there is none; owners read
// every project's keys.
export const getExternalApiLimitTargetChoices = ({
  isOwner,
  projects,
}: IExternalApiLimitTargetChoicesProps): IExternalApiLimitTargetChoices => {
  const keyProjects = isOwner
    ? projects
    : projects.filter((project) => project.permissions.canReadApiKeys);

  return {
    keyProjects,
    scopes:
      keyProjects.length > 0
        ? ORGANIZATION_EXTERNAL_API_LIMIT_TARGET_SCOPES
        : ORGANIZATION_EXTERNAL_API_LIMIT_TARGET_SCOPES.filter(
            (scope) => scope !== EExternalApiLimitTargetScope.API_KEY,
          ),
  };
};

// NOTE: A tenant changes only its own rules; the operator's are shown
// read-only, the way the server answers them (403 on PATCH / DELETE).
export const isExternalApiLimitRuleManagedByOrganization = (
  rule: IExternalApiLimitRule,
): boolean => rule.managedBy === EExternalApiLimitManagedBy.ORGANIZATION;

// NOTE: The name of what a rule targets, for a list row — the target's own
// name, falling back to its uuid when the name is gone (a deleted key).
export const getExternalApiLimitRuleTargetLabel = (
  rule: IExternalApiLimitRule,
): string | null => {
  const reference =
    rule.apiKey ?? rule.user ?? rule.project ?? rule.organization;

  return reference ? (reference.name ?? reference.uuid) : null;
};

export interface IExternalApiLimitRuleForm {
  name: string;
  targetScope: EExternalApiLimitTargetScope;
  targetUuid: string;
  bucket: EExternalApiLimitBucket;
  metric: EExternalApiLimitMetric;
  limitValue: string;
  windowAmount: string;
  windowUnit: EExternalApiLimitWindowUnit;
  endpoints: EExternalApiLimitEndpoint[];
  modelNames: string;
  isEnabled: boolean;
}

const EXTERNAL_API_LIMIT_FORM_FIELDS: readonly (keyof IExternalApiLimitRuleForm)[] =
  [
    "name",
    "targetScope",
    "targetUuid",
    "bucket",
    "metric",
    "limitValue",
    "windowAmount",
    "windowUnit",
    "endpoints",
    "modelNames",
    "isEnabled",
  ];

export enum EExternalApiLimitFormError {
  TARGET_REQUIRED = "target_required",
  BUCKET_NOT_ALLOWED = "bucket_not_allowed",
  INVALID_LIMIT = "invalid_limit",
  INVALID_WINDOW = "invalid_window",
  TOO_MANY_MODELS = "too_many_models",
  INVALID_MODEL_NAME = "invalid_model_name",
  NAME_TOO_LONG = "name_too_long",
}

export type TExternalApiLimitFormErrors = Partial<
  Record<keyof IExternalApiLimitRuleForm, EExternalApiLimitFormError>
>;

export const buildEmptyExternalApiLimitRuleForm = (
  targetScope: EExternalApiLimitTargetScope,
): IExternalApiLimitRuleForm => ({
  name: "",
  targetScope,
  targetUuid: "",
  bucket: EExternalApiLimitBucket.API_KEY,
  metric: EExternalApiLimitMetric.REQUESTS,
  limitValue: "60",
  windowAmount: "1",
  windowUnit: EExternalApiLimitWindowUnit.MINUTE,
  endpoints: [],
  modelNames: "",
  isEnabled: true,
});

export const toExternalApiLimitRuleForm = (
  rule: IExternalApiLimitRule,
): IExternalApiLimitRuleForm => {
  const window = toExternalApiLimitWindow(rule.windowSeconds ?? 60);
  const target = rule.apiKey ?? rule.user ?? rule.project ?? rule.organization;

  return {
    name: rule.name ?? "",
    targetScope: rule.targetScope,
    targetUuid: target?.uuid ?? "",
    bucket: rule.bucket,
    metric: rule.metric,
    limitValue: String(rule.limitValue),
    windowAmount: String(window.amount),
    windowUnit: window.unit,
    endpoints: rule.endpoints ?? [],
    modelNames: (rule.modelNames ?? []).join(", "),
    isEnabled: rule.isEnabled,
  };
};

// NOTE: A comma- or newline-separated list, trimmed, de-duplicated, blanks
// dropped — the same normalization the server applies.
export const parseExternalApiLimitModelNames = (raw: string): string[] => [
  ...new Set(
    raw
      .split(/[,\n]/)
      .map((modelName) => modelName.trim())
      .filter((modelName) => modelName.length > 0),
  ),
];

// NOTE: NSAI-7781 — an Arabic or Persian keyboard types Arabic-Indic (U+0660–
// U+0669) or Extended Arabic-Indic (U+06F0–U+06F9) digits, and `\d` without the
// `u` flag matches ASCII 0–9 only, so `٥` was refused as "not a whole number".
// The digits are mapped to Western ones first; the Arabic decimal and
// thousands separators map to `.` and `,`, which the pattern still refuses.
const parsePositiveInteger = (raw: string): number | null => {
  const normalized = toWesternDigits(raw.trim());

  if (!/^[0-9]+$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

const needsTarget = (targetScope: EExternalApiLimitTargetScope): boolean =>
  targetScope !== EExternalApiLimitTargetScope.PLATFORM &&
  targetScope !== EExternalApiLimitTargetScope.ORGANIZATION;

interface IValidateExternalApiLimitRuleFormProps {
  form: IExternalApiLimitRuleForm;
  // NOTE: Editing keeps the target the rule was created for — it is not
  // part of the patch, so it is not validated either.
  isTargetFixed?: boolean;
}

export const validateExternalApiLimitRuleForm = ({
  form,
  isTargetFixed = false,
}: IValidateExternalApiLimitRuleFormProps): TExternalApiLimitFormErrors => {
  const errors: TExternalApiLimitFormErrors = {};

  if (
    !isTargetFixed &&
    needsTarget(form.targetScope) &&
    form.targetUuid.trim().length === 0
  ) {
    errors.targetUuid = EExternalApiLimitFormError.TARGET_REQUIRED;
  }

  if (
    !EXTERNAL_API_LIMIT_ALLOWED_BUCKETS[form.targetScope].includes(form.bucket)
  ) {
    errors.bucket = EExternalApiLimitFormError.BUCKET_NOT_ALLOWED;
  }

  const limitValue = parsePositiveInteger(form.limitValue);

  if (limitValue === null || limitValue > EXTERNAL_API_LIMIT_MAX_LIMIT_VALUE) {
    errors.limitValue = EExternalApiLimitFormError.INVALID_LIMIT;
  }

  if (isWindowedExternalApiLimitMetric(form.metric)) {
    const amount = parsePositiveInteger(form.windowAmount);
    const windowSeconds =
      amount === null
        ? null
        : fromExternalApiLimitWindow({ amount, unit: form.windowUnit });

    if (
      windowSeconds === null ||
      windowSeconds > EXTERNAL_API_LIMIT_MAX_WINDOW_SECONDS
    ) {
      errors.windowAmount = EExternalApiLimitFormError.INVALID_WINDOW;
    }
  }

  const modelNames = parseExternalApiLimitModelNames(form.modelNames);

  if (modelNames.length > EXTERNAL_API_LIMIT_MAX_MODEL_NAMES) {
    errors.modelNames = EExternalApiLimitFormError.TOO_MANY_MODELS;
  } else if (
    modelNames.some(
      (modelName) =>
        modelName.length > EXTERNAL_API_LIMIT_MAX_MODEL_NAME_LENGTH,
    )
  ) {
    errors.modelNames = EExternalApiLimitFormError.INVALID_MODEL_NAME;
  }

  if (form.name.trim().length > EXTERNAL_API_LIMIT_MAX_NAME_LENGTH) {
    errors.name = EExternalApiLimitFormError.NAME_TOO_LONG;
  }

  return errors;
};

// NOTE: The settings half of the request body, shared by create and update.
// Call only on a form `validateExternalApiLimitRuleForm` accepted.
export const toExternalApiLimitRuleSettings = (
  form: IExternalApiLimitRuleForm,
): Omit<ICreateExternalApiLimitRuleBody, "targetScope"> => {
  const modelNames = parseExternalApiLimitModelNames(form.modelNames);
  const name = form.name.trim();

  return {
    name: name.length > 0 ? name : null,
    bucket: form.bucket,
    metric: form.metric,
    limitValue: parsePositiveInteger(form.limitValue) ?? 1,
    windowSeconds: isWindowedExternalApiLimitMetric(form.metric)
      ? fromExternalApiLimitWindow({
          amount: parsePositiveInteger(form.windowAmount) ?? 1,
          unit: form.windowUnit,
        })
      : null,
    endpoints: form.endpoints.length > 0 ? form.endpoints : null,
    modelNames: modelNames.length > 0 ? modelNames : null,
    isEnabled: form.isEnabled,
  };
};

const TARGET_UUID_FIELD: Readonly<
  Partial<
    Record<
      EExternalApiLimitTargetScope,
      "organizationUuid" | "projectUuid" | "userUuid" | "apiKeyUuid"
    >
  >
> = {
  [EExternalApiLimitTargetScope.PROJECT]: "projectUuid",
  [EExternalApiLimitTargetScope.USER]: "userUuid",
  [EExternalApiLimitTargetScope.API_KEY]: "apiKeyUuid",
};

interface IToCreateExternalApiLimitRuleBodyProps {
  form: IExternalApiLimitRuleForm;
  organizationUuid?: string;
}

// NOTE: The create body: the settings plus the uuid the target scope needs.
// The admin panel passes `organizationUuid` for an organization rule (and,
// optionally, to scope a user rule); a tenant never does.
export const toCreateExternalApiLimitRuleBody = ({
  form,
  organizationUuid,
}: IToCreateExternalApiLimitRuleBodyProps): ICreateExternalApiLimitRuleBody => {
  const targetField = TARGET_UUID_FIELD[form.targetScope];

  return {
    targetScope: form.targetScope,
    ...toExternalApiLimitRuleSettings(form),
    ...(organizationUuid !== undefined && { organizationUuid }),
    ...(targetField !== undefined && { [targetField]: form.targetUuid.trim() }),
  };
};

export interface IExternalApiLimitTargetOption {
  value: string;
  label: string;
}

// NOTE: The members a user rule can target. The members endpoint returns one
// row per project membership, so a person in three projects arrives three
// times; a rule targets the person, once.
export const toExternalApiLimitMemberOptions = (
  members: readonly IOrganizationMember[],
): IExternalApiLimitTargetOption[] => {
  const optionsByUserUuid = new Map<string, IExternalApiLimitTargetOption>();

  for (const { user } of members) {
    if (optionsByUserUuid.has(user.uuid)) {
      continue;
    }

    const fullName = [user.firstName, user.lastName]
      .filter((part): part is string => !!part && part.trim().length > 0)
      .join(" ");

    optionsByUserUuid.set(user.uuid, {
      value: user.uuid,
      label:
        fullName.length > 0
          ? fullName
          : (user.email ?? user.username ?? user.uuid),
    });
  }

  return [...optionsByUserUuid.values()];
};

// NOTE: i18next's `t` with the settings namespace, narrowed to what the two
// formatters below call — both clients pass their own `t` unchanged.
export type TExternalApiLimitTranslate = (
  key: string,
  values?: Record<string, string | number>,
) => string;

interface IExternalApiLimitRuleTextProps {
  rule: IExternalApiLimitRule;
  translate: TExternalApiLimitTranslate;
}

// NOTE: "600 requests per 1 minutes" / "At most 5 parallel requests" — the one
// line that says what a rule allows. Shared so web and mobile say it alike.
export const getExternalApiLimitRuleSummary = ({
  rule,
  translate,
}: IExternalApiLimitRuleTextProps): string => {
  const limit = formatDeviceLocaleNumber({ value: rule.limitValue });

  if (
    !isWindowedExternalApiLimitMetric(rule.metric) ||
    rule.windowSeconds === null
  ) {
    return translate("externalApiLimits.summary.concurrent", { limit });
  }

  const window = toExternalApiLimitWindow(rule.windowSeconds);

  return translate("externalApiLimits.summary.windowed", {
    limit,
    metric: translate(`externalApiLimits.metric.${rule.metric}`).toLowerCase(),
    amount: window.amount,
    // NOTE: Its own plural-aware key, so "1 minute" and "5 minutes" both read
    // right; the picker keeps the plain unit names.
    unit: translate(`externalApiLimits.summaryUnit.${window.unit}`, {
      count: window.amount,
    }),
  });
};

// NOTE: The rest of a rule on one line: its name, what it targets, how it
// counts, and the endpoint / model filters it carries.
export const getExternalApiLimitRuleDetails = ({
  rule,
  translate,
}: IExternalApiLimitRuleTextProps): string => {
  const targetLabel = getExternalApiLimitRuleTargetLabel(rule);
  const scope = translate(`externalApiLimits.scope.${rule.targetScope}`);

  return [
    rule.name,
    targetLabel ? `${scope}: ${targetLabel}` : scope,
    translate(`externalApiLimits.bucket.${rule.bucket}`),
    ...(rule.endpoints ?? []).map((endpoint) =>
      translate(`externalApiLimits.endpoint.${endpoint}`),
    ),
    ...(rule.modelNames ?? []),
  ]
    .filter((part): part is string => !!part)
    .join(" · ");
};

// NOTE: The errors an edit may have fixed, keyed by the field that shows them:
// the window error reads amount and unit together and vanishes with a
// windowless metric; a new target scope resets the target and the counter.
const FIELDS_CLEARED_BY_CHANGE: Readonly<
  Partial<
    Record<
      keyof IExternalApiLimitRuleForm,
      readonly (keyof IExternalApiLimitRuleForm)[]
    >
  >
> = {
  windowUnit: ["windowAmount"],
  metric: ["windowAmount"],
  targetScope: ["targetUuid", "bucket"],
};

interface IClearExternalApiLimitFormErrorsProps {
  errors: TExternalApiLimitFormErrors;
  patch: Partial<IExternalApiLimitRuleForm>;
}

// NOTE: Drops the errors of the fields a change touched, so a corrected field
// stops showing its old error before the next save re-validates the form.
export const clearExternalApiLimitFormErrors = ({
  errors,
  patch,
}: IClearExternalApiLimitFormErrorsProps): TExternalApiLimitFormErrors => {
  const cleared = new Set<string>();

  for (const field of EXTERNAL_API_LIMIT_FORM_FIELDS) {
    if (field in patch) {
      cleared.add(field);
      for (const related of FIELDS_CLEARED_BY_CHANGE[field] ?? []) {
        cleared.add(related);
      }
    }
  }

  return Object.fromEntries(
    Object.entries(errors).filter(([field]) => !cleared.has(field)),
  );
};
